/**
 * Narrows an unknown response body to something that carries a `token` field,
 * without asserting anything about the field's own type.
 */
function hasTokenField(value: unknown): value is { token: unknown } {
  return typeof value === 'object' && value !== null && 'token' in value;
}

/**
 * Reads the access token out of a refresh response body.
 *
 * Scorpius `RenewTokenView` answers a successful refresh with `{"token": ...}`
 * and nothing else, so a 2xx carrying any other shape is a failed refresh
 * wearing a success status. Persisting such a body writes `undefined` as the
 * access token, and `authReducer` then stamps a 60 minute expiration on it
 * because it treats a missing token as "no expiry information available". The
 * session therefore looks healthy for an hour while every request goes out as
 * `Bearer undefined`.
 *
 * Returns null instead of throwing so each caller keeps its own failure
 * contract — an outcome value, a dispatched action, or a rejected promise.
 *
 * @param data - Parsed response body from the refresh endpoint
 * @returns The access token, or null when the body carries no usable one
 */
export function readRefreshedAccessToken(data: unknown): string | null {
  if (!hasTokenField(data)) {
    return null;
  }

  const { token } = data;
  return typeof token === 'string' && token !== '' ? token : null;
}

export default readRefreshedAccessToken;
